import React from 'react';

import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router';

import { parseNullableValue } from '@hrlogics/helper-utils';
import { type SxProp } from '@hrlogics/react-helper-types';

import { type Nullable } from '@clearverify/types';

import { Badge } from 'src/components/common';
import { Avatar } from 'src/components/shared';
import { demoPrivacyBlurStyles } from 'src/constants/demoPrivacyMode';
import { useDemoPrivacyBlurred } from 'src/hooks/demoPrivacyMode';

type ClickableLabelContentProps = {
  label: Nullable<string>;
  labelPostfix?: Nullable<string>;
  logoUrl?: Nullable<string>;
  withoutLogo?: boolean;
  badgeContent?: Nullable<string>;
};

type ClickableLabelProps = (
  | { type: 'link'; to: string; onClick?: never }
  | { type: 'button'; to?: never; onClick: () => void }
) &
  ClickableLabelContentProps;

export const ClickableLabel: React.FC<ClickableLabelProps> = ({
  type,
  to,
  onClick,
  label,
  ...rest
}) => {
  if (type === 'link') {
    return (
      <Box component={Link} to={to} sx={containerStyles} aria-label={label ? label : undefined}>
        <ClickableLabelContent label={label} {...rest} />
      </Box>
    );
  }

  return (
    <Button onClick={onClick} variant="text" disableRipple disableElevation sx={containerStyles}>
      <ClickableLabelContent label={label} {...rest} />
    </Button>
  );
};

const ClickableLabelContent: React.FC<ClickableLabelContentProps> = ({
  label,
  labelPostfix,
  logoUrl,
  withoutLogo = false,
  badgeContent,
}) => {
  const isBlurred = useDemoPrivacyBlurred();

  return (
    <React.Fragment>
      {!withoutLogo && (
        <Badge invisible={!badgeContent} badgeContent={badgeContent}>
          <Avatar src={isBlurred ? null : logoUrl} variant="circular" />
        </Badge>
      )}
      <Typography
        variant="body2"
        fontWeight="bold"
        sx={isBlurred ? demoPrivacyBlurStyles : undefined}
      >
        {parseNullableValue(label)}
        {labelPostfix ? ` (${labelPostfix})` : ''}
      </Typography>
    </React.Fragment>
  );
};

const containerStyles: SxProp = theme => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,

  textAlign: 'left',
  textDecoration: 'none',
  textTransform: 'none',
  color: theme.palette.text.primary,

  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
});
